import React, { PropsWithChildren, ReactNode, CSSProperties } from 'react';
import classNames from 'classnames';
import styles from './WidgetFrame.module.scss';

export interface WidgetFrameProps {
  children: ReactNode;
  classNameGrid?: string;
  name?: string | JSX.Element;
  error?: string;
  labelTextPosition?: "left" | "center" | "right";
  labelBlockPosition?: "left" | "right" | "top" | "bottom";
  labelVerticalAlign?: "top" | "center" | "bottom";
  labelWidth?: string | number;
  childrenBlockPosition?: "left" | "center" | "right";
  childrenVerticalAlign?: "top" | "center" | "bottom";
  required?: boolean;
}

export const WidgetFrame = ({
  children,
  classNameGrid,
  name,
  error,
  labelTextPosition,
  labelBlockPosition,
  labelVerticalAlign = "top",
  labelWidth,
  childrenBlockPosition,
  childrenVerticalAlign = "top",
  required
}: PropsWithChildren<WidgetFrameProps>) => {
  const numberOfChildren = React.Children.count(children);

  const getLabelAlignmentClass = (): string => {
    if (labelTextPosition === "right") {
      return styles.name_right;
    } else if (labelTextPosition === "center") {
      return styles.name_center;
    } else {
      return styles.name_left;
    }
  };

  const getLabelVerticalAlignClass = (): string => {
    // L'alignement vertical n'a de sens que pour les positions left/right
    if (labelBlockPosition === "top" || labelBlockPosition === "bottom") {
      return "";
    }
    
    if (labelVerticalAlign === "center") {
      return styles.widget_frame_name_valign_center;
    } else if (labelVerticalAlign === "bottom") {
      return styles.widget_frame_name_valign_bottom;
    } else {
      return styles.widget_frame_name_valign_top;
    }
  };

  const getChildrenAlignmentClass = (): string => {
    if (childrenBlockPosition === "right") {
      return styles.children_right;
    } else if (childrenBlockPosition === "center") {
      return styles.children_center;
    } else {
      return styles.children_left;
    }
  };

  const getChildrenVerticalAlignClass = (): string => {
    // L'alignement vertical n'a de sens que pour les positions left/right
    if (labelBlockPosition === "top" || labelBlockPosition === "bottom") {
      return "";
    }
    
    if (childrenVerticalAlign === "center") {
      return styles.widget_frame_element_valign_center;
    } else if (childrenVerticalAlign === "bottom") {
      return styles.widget_frame_element_valign_bottom;
    } else {
      return styles.widget_frame_element_valign_top;
    }
  };

  const nameAlignmentClass = getLabelAlignmentClass();
  const nameVerticalAlignClass = getLabelVerticalAlignClass();
  const childrenAlignmentClass = getChildrenAlignmentClass();
  const childrenVerticalAlignClass = getChildrenVerticalAlignClass();

  const renderLabelFromName = (): JSX.Element => {
    if (name) {
      return (
        <>
          {name}
          {required && (
            <i className={classNames(styles.widget_frame_required)}>&nbsp;&#1645;</i>
          )}
        </>
      );
    }
    return <></>;
  };

  const renderLabelFromFirstChild = (): JSX.Element => {
    const child = React.Children.toArray(children)[0];
    return <>{child}</>;
  };

  const renderLabel = (): JSX.Element => {
    if (name) {
      return renderLabelFromName();
    }
    return renderLabelFromFirstChild();
  };

  const renderElement = (): JSX.Element => {
    const child = React.Children.toArray(children)[numberOfChildren - 1];
    return (
      <div className={classNames(styles.widget_frame_element_child, childrenAlignmentClass)}>
        {child}
      </div>
    );
  };

  const renderComponentLayout1LabelLeft = (): JSX.Element => {
    return (
      <>
        <div className={classNames(
          styles.widget_frame_name, 
          nameAlignmentClass, 
          nameVerticalAlignClass,
          styles.widget_frame_layout_1_position_1_1
        )}>
          {renderLabel()}
        </div>
        <div className={classNames(
          styles.widget_frame_element, 
          childrenVerticalAlignClass,
          styles.widget_frame_layout_1_position_1_2
        )}>
          {renderElement()}
        </div>
        {error && <div className={classNames(styles.widget_frame_info, styles.widget_frame_layout_1_position_2)}>{error}</div>}
      </>
    );
  };

  const renderComponentLayout1LabelRight = (): JSX.Element => {
    return (
      <>
        <div className={classNames(
          styles.widget_frame_name, 
          nameAlignmentClass, 
          nameVerticalAlignClass,
          styles.widget_frame_layout_1_position_1_2
        )}>
          {renderLabel()}
        </div>
        <div className={classNames(
          styles.widget_frame_element, 
          childrenVerticalAlignClass,
          styles.widget_frame_layout_1_position_1_1
        )}>
          {renderElement()}
        </div>
        {error && <div className={classNames(styles.widget_frame_info, styles.widget_frame_layout_1_position_2)}>{error}</div>}
      </>
    );
  };

  const renderComponentLayout2LabelTop = (): JSX.Element => {
    return (
      <>
        <div className={classNames(styles.widget_frame_name, nameAlignmentClass, styles.widget_frame_layout_2_position_1)}>
          {renderLabel()}
        </div>
        <div className={classNames(styles.widget_frame_element, styles.widget_frame_layout_2_position_2)}>
          {renderElement()}
        </div>
        {error && <div className={classNames(styles.widget_frame_info, styles.widget_frame_layout_2_position_3)}>{error}</div>}
      </>
    );
  };

  const renderComponentLayout3LabelBottom = (): JSX.Element => {
    return (
      <>
        <div className={classNames(styles.widget_frame_element, styles.widget_frame_layout_2_position_1)}>
          {renderElement()}
        </div>
        <div className={classNames(styles.widget_frame_name, nameAlignmentClass, styles.widget_frame_layout_2_position_2)}>
          {renderLabel()}
        </div>
        {error && <div className={classNames(styles.widget_frame_info, styles.widget_frame_layout_2_position_3)}>{error}</div>}
      </>
    );
  };

  const renderComponent = (): JSX.Element => {
    if (labelBlockPosition === "right") {
      return renderComponentLayout1LabelRight();
    } else if (labelBlockPosition === "top") {
      return renderComponentLayout2LabelTop();
    } else if (labelBlockPosition === "bottom") {
      return renderComponentLayout3LabelBottom();
    } else {
      return renderComponentLayout1LabelLeft();
    }
  };

  const gridStyle: CSSProperties = {};
  if (labelWidth && labelBlockPosition !== "top" && labelBlockPosition !== "bottom") {
    const width = typeof labelWidth === "number" ? `${labelWidth}px` : labelWidth;
    gridStyle.gridTemplateColumns = `${width} auto`;
  }

  return (
    <div
      className={classNameGrid || classNames(styles.widget_frame, error && styles.widget_frame_has_error)}
      style={gridStyle}
    >
      {renderComponent()}
    </div>
  );
};

export default WidgetFrame;